import { create } from "zustand";
import { persist } from "zustand/middleware";
import { v4 as uuid } from "uuid";
import { getPriceForSize, type ProductPrice, type ProductSizePrice } from "../lib/api";

export interface GuestProduct {
  id: string;
  name: string;
  slug: string;
  price: number;
  prices: ProductPrice[];
  sizePrices?: ProductSizePrice[];
  imageUrl: string;
  stock: number;
}

export interface GuestCartItem {
  id: string; // local item id
  product: GuestProduct;
  quantity: number;
  size: string | null;
}

interface GuestCartStore {
  items: GuestCartItem[];
  open: boolean;
  add: (product: GuestProduct, quantity?: number, size?: string | null) => void;
  update: (id: string, quantity: number) => void;
  remove: (id: string) => void;
  clear: () => void;
  toggleOpen: () => void;
}

export const useGuestCartStore = create<GuestCartStore>()(
  persist(
    (set, get) => ({
      items: [],
      open: false,

      add: (product, quantity = 1, size = null) => {
        const existing = get().items.find(
          (i) => i.product.id === product.id && i.size === size
        );
        if (existing) {
          set((s) => ({
            items: s.items.map((i) =>
              i.product.id === product.id && i.size === size
                ? { ...i, quantity: Math.min(i.product.stock, i.quantity + quantity) }
                : i
            ),
          }));
        } else {
          set((s) => ({
            items: [...s.items, { id: uuid(), product, quantity, size }],
          }));
        }
      },

      update: (id, quantity) => {
        if (quantity < 1) {
          set((s) => ({ items: s.items.filter((i) => i.id !== id) }));
        } else {
          set((s) => ({
            items: s.items.map((i) =>
              i.id === id ? { ...i, quantity: Math.min(i.product.stock, quantity) } : i
            ),
          }));
        }
      },

      remove: (id) => set((s) => ({ items: s.items.filter((i) => i.id !== id) })),
      clear: () => set({ items: [] }),
      toggleOpen: () => set((s) => ({ open: !s.open })),
    }),
    { name: "chlolo-guest-cart" }
  )
);

export function guestCartTotal(items: GuestCartItem[], currency = "USD") {
  return items.reduce((sum, item) => {
    return sum + getPriceForSize(item.product, item.size, currency) * item.quantity;
  }, 0);
}
